/**
 * @file MenuLanding.tsx
 * @description
 * -----------------------------------------------------
 * Archivo que contiene el componente de Nav
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-01-27
 * @modified 2026-01-26
 * @author Correcta
 * @author Leonard Vasilescu
 * @modified Sergio Manjón
 * @copyright © Correcta
 * @license Private
 * */
"use client";

import { useLocale } from "next-intl";
import { Link, useRouter, usePathname } from "@/i18n/routing";
import { useTranslations } from "use-intl";
import SelectComponent from "@/components/ui/SelectComponent";
import { LANGUAGES } from "@/constants/languages";
import DarkModeButton from "@/components/theme/DarkModeButton";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "@/context/ThemeProvider";
import ImageComponent from "@/components/ui/ImageComponent";
import { BUTTON_STATUS, TYPE_BUTTON } from "@/constants/button";
import { X } from "lucide-react";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { LanguagesCodeWithName } from "@/constants/languages";
import { Globe } from "lucide-react";

interface MenuLandingProps {
  token?: string;
  logoHref?: string;
}

const MenuLanding = ({ token, logoHref = "/" }: MenuLandingProps) => {
  const t = useTranslations("NavComponent");
  const languages = useTranslations("Languages");
  const ariaLabel = useTranslations("AriaLabel");
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isDark } = useTheme();

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLang = e.target.value;
    const cleanPath = `/${pathname.replace(`/${locale}`, "").replace(/^\//, "")}`;
    const query = token ? `?token=${encodeURIComponent(token)}` : "";
    router.push(`${cleanPath}${query}`, { locale: selectedLang });
  };

  const navMenuRef = useRef<HTMLUListElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const languageConfig = {
    es: { icon: "/assets/svg/language-spanish.svg", label: languages("es") },
    en: { icon: "/assets/svg/language-english.svg", label: languages("en") },
    fr: { icon: "/assets/svg/language-french.svg", label: languages("fr") },
    de: { icon: "/assets/svg/language-german.svg", label: languages("de") },
    pt: { icon: "/assets/svg/language-portuguese.svg", label: languages("pt") },
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        navMenuRef.current &&
        !navMenuRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  return (
    <nav className={"navbar"}>
      <Link href={logoHref}>
        <picture>
          <source
            media="(max-width: 768px)"
            srcSet={
              isDark
                ? "/assets/svg/symbol-negative.svg"
                : "/assets/svg/symbol.svg"
            }
          />
          <ImageComponent
            src={"/assets/svg/brand.svg"}
            srcDark={"/assets/svg/brand-negative.svg"}
            zoomable={false}
            alt={"logoCorrecta"}
            width={183}
            height={55}
            priority
          />
        </picture>
      </Link>

      <Link className="btn btn--outline loginBoton" href={"/login"}>
        {t("login")}
      </Link>

      {/* Botón hamburguesa (se muestra solo en phone por CSS) */}
      <ButtonComponent
        type={TYPE_BUTTON}
        status={BUTTON_STATUS.PRIMARY}
        label=""
        onClick={() => setIsMenuOpen((v) => !v)}
        className="navbar__toggle"
        ariaLabel={ariaLabel("openMenu")}
        ariaExpanded={isMenuOpen}
        ref={menuButtonRef}
      >
        <span className="navbar__toggle-bar" />
        <span className="navbar__toggle-bar" />
        <span className="navbar__toggle-bar" />
      </ButtonComponent>

      <ul
        id="nav-menu"
        className={`navbar__menu ${isMenuOpen ? "is-open" : ""}`}
        hidden={!isMenuOpen}
        ref={navMenuRef}
      >
        <li className="navbar__menu-item navbar__menu-item--close">
          <ButtonComponent
            className="navbar__menu--close-button"
            type={TYPE_BUTTON}
            status={BUTTON_STATUS.ICON}
            onClick={() => setIsMenuOpen(false)}
            ariaLabel={ariaLabel("closeModal")}
            ref={menuButtonRef}
          >
            <X />
          </ButtonComponent>
        </li>
        <li className="navbar__menu-item navbar__languageMovil--container--darkMode">
          <DarkModeButton ariaLabel={ariaLabel("darkModeMenu")} />
          <section className="language--movile">
            <span>
              <Globe size={24} role="img" aria-hidden="true" />
              <span>
                {languageConfig[locale as keyof typeof languageConfig].label}
              </span>
            </span>
          </section>
        </li>
        <li className="navbar__menu-item">
          <Link className="btn btn--outline" href={"/login"}>
            {t("login")}
          </Link>
        </li>
        <li className="navbar__menu-item navbar__language">
          <SelectComponent
            id="language"
            name="language"
            options={LANGUAGES}
            value={locale}
            onChange={handleChange}
          />
        </li>
        <li className="navbar__menu-item navbar__languageMovil">
          <ul>
            {LanguagesCodeWithName.map((language) => (
              <li key={language.value}>
                <button
                  className="navbar__languageMovil--botonLanguage"
                  onClick={() =>
                    handleChange({
                      target: { value: language.value },
                    } as React.ChangeEvent<HTMLSelectElement>)
                  }
                >
                  <ImageComponent
                    src={
                      languageConfig[
                        language.value as keyof typeof languageConfig
                      ].icon
                    }
                    alt={
                      languageConfig[
                        language.value as keyof typeof languageConfig
                      ].label
                    }
                    zoomable={false}
                    noTranslation={true}
                  />
                  <span>
                    {
                      languageConfig[
                        language.value as keyof typeof languageConfig
                      ].label
                    }
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </li>
      </ul>
    </nav>
  );
};

export default MenuLanding;
